/**
 * 分包合同双签共用入口：项目卡片、分包商工作台及 CEO 首页使用同一服务端状态。
 * 只根据服务端签署回执判断各方是否已签；历史单签原件继续保留，新流程必须双方签完才显示完成。
 */
import React, { useEffect, useState } from "react";
import { FileSignature, ArrowRight, RefreshCw } from "lucide-react";
import { useApp } from "../context/AppContext";

export const signingParties = packet => {
  const receipts = packet?.receipts || [];
  const signed = party => receipts.some(r => r.signerParty === party ||
    (!r.signerParty && (party === "ceo" ? r.role === "ceo" || r.username === packet?.ceoUser : r.role === "subcontractor" || r.username === packet?.subUser)));
  return { sub: signed("sub"), ceo: signed("ceo") };
};

export const signingLabel = (status, zh, packet) => {
  const { ceo } = signingParties(packet);
  if (status === "sub_pending") return ceo
    ? (zh ? "CEO 已签 · 待分包商签署" : "CEO signed · Awaiting subcontractor")
    : (zh ? "待双方签署 · 可任意先后" : "Awaiting both signatures · Either may sign first");
  return ({
    preparing: zh ? "正在准备固定版本" : "Preparing document",
    ceo_pending: zh ? "分包商已签 · 待 CEO 签署" : "Subcontractor signed · Awaiting CEO",
    completed: zh ? "双方已签署" : "Both parties signed",
    withdrawn: zh ? "已撤回 · 原记录保留" : "Withdrawn · Records retained",
    failed: zh ? "准备失败 · 请重试" : "Preparation failed · Retry",
  }[status] || (zh ? "尚未发起双签" : "Signing not started"));
};

export function SubcontractSigningSummary({ sc, zh }) {
  const state = sc.signing?.status;
  return <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`dual-sign-summary-${sc.id}`}>
    <div className="flex items-center gap-2 min-w-0">
      <FileSignature className="h-5 w-5 text-brand-500 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800">{zh ? "合同签署" : "Contract signing"}</p>
        <p className={`text-xs ${state === "completed" ? "text-emerald-700" : "text-slate-500"}`}>
          {state ? signingLabel(state, zh, sc.signing) : sc.esignStatus === "signed" ? (zh ? "历史已签合同 · 可主动发起重新签署" : "Historical signed contract · Re-sign available") : (zh ? "分包商与 CEO 均可先签，双方签完后归档" : "Subcontractor and CEO may sign in either order; archive follows both signatures")}
          {state === "completed" && (sc.signing.syncPending ? (zh ? " · 归档／通知待同步" : " · Delivery pending") : (zh ? " · 已归档" : " · Archived"))}
        </p>
      </div>
    </div>
    <a href={`/subcontracts/${sc.id}/signing`} className="min-h-[44px] inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-600 focus-visible:ring-2 ring-brand-300">
      {zh ? (state ? "查看签署" : "管理签署") : (state ? "View signing" : "Manage signing")}<ArrowRight className="h-4 w-4" />
    </a>
  </div>;
}

export function SubcontractSigningInbox() {
  const { api, lang } = useApp();
  const zh = lang === "zh", [items, setItems] = useState([]), [error, setError] = useState("");
  const load = () => api.get("/subcontract-signing/inbox").then(({ data }) => { setItems(data); setError(""); }).catch(() => setError(zh ? "签署待办加载失败，请重试" : "Unable to load signature requests"));
  // CEO 看板保持与合同当前签署阶段同步；站内状态轮询补足跨浏览器/跨用户无法触发的本地事件。
  useEffect(() => { load(); const timer = window.setInterval(load, 15000); return () => window.clearInterval(timer); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ceo-subcontract-signing-inbox">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-base font-bold flex items-center gap-2"><FileSignature className="h-5 w-5 text-brand-500" />{zh ? "分包合同待签署" : "Subcontracts awaiting signature"} <span className="text-brand-600">{items.length}</span></h2>
      <button onClick={load} className="min-h-[44px] min-w-[44px] grid place-items-center rounded-lg hover:bg-slate-50" aria-label={zh ? "刷新签署待办" : "Refresh signing requests"}><RefreshCw className="h-4 w-4" /></button>
    </div>
    {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : !items.length ? <p className="text-sm text-slate-500">{zh ? "暂无待您签署的分包合同" : "No subcontracts require your signature"}</p> :
      items.map(item => <a key={item.id} href={`/subcontracts/${item.id}/signing`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50 min-h-[56px]">
        <div className="min-w-0"><p className="font-semibold text-sm break-words">{item.address || item.id}</p><p className="text-xs text-slate-500">{item.tradeName} · {item.subcontractorName}</p><p className="text-xs text-brand-600 mt-1">{signingLabel(item.packet?.status, zh, item.packet)}</p></div><ArrowRight className="h-4 w-4 shrink-0 text-brand-500" />
      </a>)}
  </section>;
}
